// Create a reusable user avatar component
import React, { useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { createPublicAvatarUrl } from '../config/avatar';
import { fetchWithAuth } from '../utils/api';

interface UserAvatarProps {
  username: string;
  avatarUrl?: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  forceFetch?: boolean;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  username, 
  avatarUrl,
  size = "md",
  className = "",
  forceFetch = false
}) => {
  const [actualAvatarUrl, setActualAvatarUrl] = useState<string | null>(avatarUrl || null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Size classes with outline
  const sizeClasses = {
    sm: "h-8 w-8 border-2 border-solid",
    md: "h-10 w-10 border-2 border-solid",
    lg: "h-14 w-14 border-2 border-solid",
    xl: "h-20 w-20 border-2 border-solid"
  };

  // Fetch user profile to get avatar URL if not provided
  useEffect(() => {
    const fetchAvatar = async () => {
      // If we have a direct avatarUrl prop and we're not forcing a fetch, use it
      if (avatarUrl && !forceFetch) {
        // If the avatarUrl is a profiles path, convert to public URL
        if (avatarUrl.includes('profiles/')) {
          const filename = avatarUrl.split('profiles/')[1];
          setActualAvatarUrl(createPublicAvatarUrl(filename));
        } else {
          setActualAvatarUrl(avatarUrl);
        }
        return;
      }
      
      setIsLoading(true);
      setError(false);
      
      try {
        // First, try to fetch the user's profile to get their avatar URL
        const endpoint = `/users/${username}/`;
        const userData = await fetchWithAuth(endpoint);
        
        if (userData && userData.avatar_url) {
          // If the avatar URL points to Supabase storage (contains profiles/), convert to public URL
          if (userData.avatar_url.includes('profiles/')) {
            const filename = userData.avatar_url.split('profiles/')[1];
            const publicUrl = createPublicAvatarUrl(filename);
            setActualAvatarUrl(publicUrl);
          } else {
            // If not a Supabase URL, use the raw URL
            setActualAvatarUrl(userData.avatar_url);
          }
        } else {
          // Use a default avatar
          setActualAvatarUrl(createPublicAvatarUrl("avatar1.svg"));
        }
      } catch (err) {
        console.error(`Error fetching avatar for user ${username}:`, err);
        // Use a default avatar on error
        setActualAvatarUrl(createPublicAvatarUrl("avatar1.svg"));
        setError(false); // We're not setting error to true since we have a fallback
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatar();
  }, [username, avatarUrl, forceFetch]);
  
  // Handle error on image load
  const handleImageError = () => {
    console.error(`Avatar image failed to load: ${actualAvatarUrl}`);
    setError(true);
    // Use a default avatar on error
    setActualAvatarUrl(createPublicAvatarUrl("avatar1.svg"));
  };
  
  // First letter of username for fallback
  const firstLetter = username ? username.charAt(0).toUpperCase() : "U";
  
  return (
    <Avatar className={`${sizeClasses[size]} ${className} avatar-outline`}>
      {actualAvatarUrl && !error ? (
        <AvatarImage 
          src={actualAvatarUrl} 
          alt={username} 
          onError={handleImageError} 
        />
      ) : null}
      <AvatarFallback>{isLoading ? "..." : firstLetter}</AvatarFallback>
    </Avatar>
  );
};